import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { LoginRequest, AuthState } from '../types'
import { authApi } from '../api/auth'
import { tokenManager } from '../api/instance'
import { canWrite as canWriteCheck } from '../utils/permissions'

interface AuthContextType extends AuthState {
    login: (credentials: LoginRequest) => Promise<void>
    logout: () => Promise<void>
    refreshUser: () => Promise<void>
    /** True if the current user can create, edit, delete resources (false for Viewer role) */
    canWrite: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()
    const [state, setState] = useState<AuthState>({
        user: null,
        accessToken: tokenManager.getAccessToken(),
        refreshToken: tokenManager.getRefreshToken(),
        isAuthenticated: false,
        isLoading: true,
    })

    const refreshUser = useCallback(async () => {
        try {
            const accessToken = tokenManager.getAccessToken()
            if (!accessToken) {
                setState({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    isLoading: false,
                })
                return
            }

            const user = await authApi.me()
            setState({
                user,
                accessToken,
                refreshToken: tokenManager.getRefreshToken(),
                isAuthenticated: true,
                isLoading: false,
            })
        } catch (error) {
            // Token might be invalid, clear everything
            tokenManager.clearTokens()
            setState({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
            })
        }
    }, [])

    useEffect(() => {
        refreshUser()
    }, [refreshUser])

    const login = useCallback(async (credentials: LoginRequest) => {
        setState((prev) => ({ ...prev, isLoading: true }))
        try {
            // Clear cache before login to ensure fresh data for new user
            queryClient.clear()
            const response = await authApi.login(credentials)
            setState({
                user: response.user,
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
                isAuthenticated: true,
                isLoading: false,
            })
        } catch (error) {
            setState((prev) => ({ ...prev, isLoading: false }))
            throw error
        }
    }, [queryClient])

    const logout = useCallback(async () => {
        setState((prev) => ({ ...prev, isLoading: true }))
        try {
            await authApi.logout()
        } finally {
            // Clear all React Query cache to prevent data leaking between users
            queryClient.clear()
            setState({
                user: null,
                accessToken: null,
                refreshToken: null,
                isAuthenticated: false,
                isLoading: false,
            })
        }
    }, [queryClient])

    const canWrite = canWriteCheck(state.user?.role)

    const contextValue = useMemo(() => ({
        ...state, login, logout, refreshUser, canWrite,
    }), [state, login, logout, refreshUser, canWrite])

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export default AuthContext
